import { useState } from "react";
import { Loader2, AlertTriangle, Mail } from "lucide-react";
import { api } from "@/lib/api";

export default function ManualScraperLogin() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await api<{ ok: true }>("/manual-scraper/login", {
      method: "POST",
      body: JSON.stringify({ email: email.trim() }),
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error ?? "Could not send sign-in link.");
      return;
    }
    setSent(true);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 dark:bg-slate-950">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {sent ? (
          <div className="text-center">
            <Mail className="mx-auto h-8 w-8 text-emerald-600" />
            <h1 className="mt-3 text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              Check your email
            </h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              If <strong>{email}</strong> is on the manual-scraper roster, we just
              sent a one-time sign-in link. The link expires in 15 minutes.
            </p>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setEmail("");
              }}
              className="mt-6 text-xs text-slate-500 underline-offset-4 hover:text-slate-900 hover:underline"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <>
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                Manual scraper portal
              </h1>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                Enter your email — we'll send a one-time sign-in link.
              </p>
            </div>

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                />
              </div>

              {error && (
                <p
                  className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
                  role="alert"
                >
                  <AlertTriangle className="h-4 w-4" />
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || !email}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Send sign-in link
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
